<template>
  <th class="text-start" :aria-sort="ariaSort">
    <button
      v-if="sortParams"
      type="button"
      class="flex w-full cursor-pointer items-center focus-visible:text-effect-focused focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
      @click="toggleSort"
    >
      {{ label }}
      <MdiSvg
        v-if="sortIcon"
        :path="sortIcon"
        size="16"
        class="ml-1"
        :class="{
          'text-disabled': !active,
        }"
      />
    </button>
    <div v-else class="flex items-center">
      {{ label }}
    </div>
  </th>
</template>

<script setup lang="ts" generic="SortKey extends string, SortDir extends SortDirection">
import { mdiMenuUp, mdiMenuDown } from '@mdi/js'
import { MdiSvg } from '@ramp106/omrjs-core-ui'
import { computed } from 'vue'

import type { SortParams, SortDirection } from './types.ts'

const props = defineProps<{
  label: string
  sortParams?: SortParams<SortKey, SortDir>
}>()

const emit = defineEmits<{
  (e: 'sort', params: SortParams<SortKey, SortDir>): void
}>()

const sortIcon = computed(() => {
  if (!props.sortParams) {
    return null
  }
  return props.sortParams.sortDirection === 'asc' ? mdiMenuUp : mdiMenuDown
})

const active = computed(() => !!props.sortParams?.sortDirection)

const ariaSort = computed(() => {
  if (!props.sortParams) return undefined
  if (props.sortParams.sortDirection === 'asc') return 'ascending'
  if (props.sortParams.sortDirection === 'desc') return 'descending'
  return 'none'
})

function toggleSort() {
  if (!props.sortParams) {
    return
  }

  const newDirection = (props.sortParams.sortDirection === 'asc' ? 'desc' : 'asc') as SortDir
  emit('sort', { sortBy: props.sortParams.sortBy, sortDirection: newDirection })
}
</script>
